import { useIsOrgReadOnly } from '@/components/auth/claimsUtils';
import RequirePalmettoFinanceUser, { palmettoFinanceOrgAlias } from '@/components/auth/RequirePalmettoFinanceUser';
import { isPalmettoFinanceUser } from '@/components/auth/RequirePalmettoFinanceUser';
import { Search } from '@/components/filters/Search';
import { getPhoneNumberUpdate } from '@/helpers/getPhoneNumberUpdate';
import usePermissions from '@/hooks/usePermissions';
import {
  Box,
  Button,
  Card,
  FormikSelectInput,
  FormikTextInput,
  FormikToggle,
  Icon,
  Modal,
  Table,
  toast,
  useBreakpoint,
  useOpenClose,
} from '@palmetto/palmetto-components';
import { Field, Form, Formik } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';

import { ProgramType, USER_PHONE_NUMBER_PATTERN, UserPermissions } from 'types';

import { getUserFacingErrorMessage } from '../../../services/helpers';
import { useGetOrganizationDetailsQuery, useSearchUsersQuery } from '../../../services/organizations';
import { useAddUserMutation, useEditUserMutation, useRemoveUserMutation } from '../../../services/users';
import { Auth0PalmettoFinanceUser } from '../../../types/Organizations';
import { useAuth } from '../../auth/authProvider';
import RequirePermissions from '../../auth/requirePermissions';
import DateTime from '../../DateTime';

import { PermissionsList } from './components/PermissionsList';

const INTERNAL_ORGANIZATION = palmettoFinanceOrgAlias;

const PHONE_NUMBER_ERROR = 'Enter a full phone number, including area code';
const PHONE_NUMBER_REGEX = new RegExp(USER_PHONE_NUMBER_PATTERN);

// Permissions always shown as hardcoded base options — excluded from the dynamic permission map
const BASE_PERMISSIONS = new Set([
  UserPermissions.editor,
  UserPermissions.reader,
  UserPermissions.comfortPlanDealer,
]);

const EXTERNAL_ALLOWED_PERMISSIONS = new Set([
  UserPermissions.admin,
  UserPermissions.lightReachOrganizationSignatory,
  UserPermissions.orgAccountingAdmin,
  UserPermissions.packageAnalyzer,
]);

const RESTRICTED_PERMISSIONS = new Set([
  UserPermissions.lightReachRequirementAdmin,
  UserPermissions.canApproveInstall,
  UserPermissions.canApproveActivation,
  UserPermissions.underwritingAdmin,
]);

interface AddUserProps {
  handleClose: () => void;
  organizationAlias: string;
  organizationProgramTypes: ProgramType[];
}

export const AddUser = ({ handleClose, organizationAlias, organizationProgramTypes }: AddUserProps) => {
  const [addUser] = useAddUserMutation();
  const userPermissions = usePermissions();
  const { claims } = useAuth();
  const isInternalUser = isPalmettoFinanceUser(claims);

  const { comfortPlanDealerConditions } = useFlags();

  const handleValidation = (values: any) => {
    const errors = {} as any;
    // TODO: validate email is an email address
    if (!values.email) {
      errors.email = 'An email is required';
    }
    if (!values.machine) {
      if (!values.givenName?.trim()) {
        errors.givenName = 'First name is required';
      }
      if (!values.familyName?.trim()) {
        errors.familyName = 'Last name is required';
      }
      const phoneNumber = values.phoneNumber?.trim();
      if (phoneNumber && !PHONE_NUMBER_REGEX.test(phoneNumber)) {
        errors.phoneNumber = PHONE_NUMBER_ERROR;
      }
    }
    if (!values.permissions || values.permissions?.length < 1) {
      errors.permissions = 'Please select at least one permission';
    } else if (
      values.permissions.length > 1 &&
      values.permissions.find((p: { value: string }) => p.value === UserPermissions.reader)
    ) {
      errors.permissions = 'The Read Only permission cannot be combined with other permissions';
    }
    return errors;
  };

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    try {
      const permissionsValues = Array.isArray(values.permissions)
        ? values.permissions.map((p: { value: string; name: string }) => p.value)
        : [values.permissions.value];

      await addUser({
        email: values.email,
        organizationAlias: organizationAlias,
        permissions: permissionsValues,
        machine: values.machine,
        ...(values.machine
          ? {}
          : {
              givenName: values.givenName.trim(),
              familyName: values.familyName.trim(),
              ...(values.phoneNumber?.trim() ? { phoneNumber: values.phoneNumber.trim() } : {}),
            }),
      }).unwrap();

      toast.success('User added');
      handleClose();
    } catch (e: unknown) {
      toast.error(getUserFacingErrorMessage(e, 'Error adding user'));
    }
    setSubmitting(false);
  };
  const showComfortPlanDealer =
    comfortPlanDealerConditions &&
    (organizationProgramTypes.includes(ProgramType.hvac) ||
      organizationProgramTypes.includes(ProgramType.waterHeater) ||
      organizationProgramTypes.includes(ProgramType.generator));

  const permissionOptions = useMemo(
    () =>
      [
        { value: UserPermissions.editor, label: 'Editor' },
        { value: UserPermissions.reader, label: 'Read Only' },
        ...(showComfortPlanDealer
          ? [{ value: UserPermissions.comfortPlanDealer, label: 'Comfort Plan Dealer' }]
          : []),
        ...userPermissions
          .filter(
            (perm) =>
              !BASE_PERMISSIONS.has(perm) &&
              !RESTRICTED_PERMISSIONS.has(perm) &&
              (isInternalUser && organizationAlias !== INTERNAL_ORGANIZATION
                ? EXTERNAL_ALLOWED_PERMISSIONS.has(perm)
                : true),
          )
          .map((perm) => ({
            value: perm,
            label: permissionMapper[perm] || perm,
          })),
      ].toSorted((a, b) => a.label.localeCompare(b.label)),
    [isInternalUser, organizationAlias, userPermissions, showComfortPlanDealer],
  );

  const initialValues = useMemo(() => {
    return {
      email: '',
      machine: false,
      givenName: '',
      familyName: '',
      phoneNumber: '',
    };
  }, []);

  return (
    <Formik
      initialValues={initialValues}
      validate={handleValidation}
      validateOnChange={false}
      onSubmit={handleSubmit}
      enableReinitialize={true}
    >
      {({ isSubmitting, values }) => (
        <Form noValidate id="addUserForm">
          <Modal.Body background="secondary">
            <Box childGap="lg">
              <Field
                type="email"
                label="Email Address"
                name="email"
                id="email"
                component={FormikTextInput}
                isRequired
                autoComplete="off"
                isDisabled={isSubmitting}
              />
              {!values.machine && (
                <Field
                  label="First Name"
                  name="givenName"
                  id="givenName"
                  component={FormikTextInput}
                  isRequired
                  autoComplete="off"
                  isDisabled={isSubmitting}
                />
              )}
              {!values.machine && (
                <Field
                  label="Last Name"
                  name="familyName"
                  id="familyName"
                  component={FormikTextInput}
                  isRequired
                  autoComplete="off"
                  isDisabled={isSubmitting}
                />
              )}
              {!values.machine && (
                <Field
                  label="Phone Number"
                  name="phoneNumber"
                  id="phoneNumber"
                  component={FormikTextInput}
                  autoComplete="off"
                  isDisabled={isSubmitting}
                  helpText="Include area code (e.g. 555-555-1234 or +1-555-555-1234)"
                />
              )}

              <Field
                label="Permissions"
                name="permissions"
                id="permissions"
                isMulti
                options={permissionOptions}
                component={FormikSelectInput}
                isDisabled={isSubmitting}
                isRequired
                menuPortalTarget={document.body}
                helpText={
                  showComfortPlanDealer ? (
                    <Box fontSize="xs">
                      Note: Comfort Plan Dealer only affects the account creation flow. Users still need Editor or
                      other permissions to perform necessary operations.
                    </Box>
                  ) : undefined
                }
              />
              <RequirePalmettoFinanceUser>
                <Field
                  label="Machine to Machine"
                  name="machine"
                  id="machine"
                  component={FormikToggle}
                  isDisabled={isSubmitting || userPermissions.includes(UserPermissions.orgAccountingAdmin)}
                />
              </RequirePalmettoFinanceUser>
            </Box>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" tone="neutral" size="md" onClick={handleClose} isDisabled={isSubmitting}>
              Cancel
            </Button>
            <Button variant="primary" size="md" type="submit" form="addUserForm" isLoading={isSubmitting}>
              Add User
            </Button>
          </Modal.Footer>
        </Form>
      )}
    </Formik>
  );
};

interface EditUserProps {
  handleClose: () => void;
  onSuccess: (updatedUser?: Auth0PalmettoFinanceUser) => void;
  user?: Auth0PalmettoFinanceUser;
  organizationAlias: string;
  organizationProgramTypes: ProgramType[];
}

export const EditUser = ({
  handleClose,
  onSuccess,
  user: userToEdit = {} as Auth0PalmettoFinanceUser,
  organizationAlias,
  organizationProgramTypes,
}: EditUserProps) => {
  const [editUser] = useEditUserMutation();
  const userPermissions = usePermissions();

  const { claims } = useAuth();
  const isInternalUser = isPalmettoFinanceUser(claims);
  const userToEditOrganization = userToEdit?.organization;
  const internalUserIsEditingExternalUser = isInternalUser && userToEditOrganization !== INTERNAL_ORGANIZATION;

  const isAdminUser = userPermissions.includes(UserPermissions.admin);
  const isOrgAccountingAdminUser = userPermissions.includes(UserPermissions.orgAccountingAdmin);
  const editUserHasOrgAccountingAdmin = userToEdit.permissions?.find(
    (up: any) => up.name === UserPermissions.orgAccountingAdmin,
  );
  const { comfortPlanDealerConditions } = useFlags();
  const showComfortPlanDealer =
    comfortPlanDealerConditions &&
    (organizationProgramTypes.includes(ProgramType.hvac) ||
      organizationProgramTypes.includes(ProgramType.waterHeater) ||
      organizationProgramTypes.includes(ProgramType.generator));

  // Human users must have both names, so anyone who can edit this user may fill in a missing one.
  const isMissingName = userToEdit.type !== 'machine' && (!userToEdit.givenName || !userToEdit.familyName);
  const canEditNames = isAdminUser || isMissingName;

  const allowsAction = useIsOrgReadOnly();
  const handleValidation = (values: any) => {
    const errors = {} as any;
    if (canEditNames && userToEdit.type !== 'machine') {
      if (!values.givenName?.trim()) errors.givenName = 'First name is required';
      if (!values.familyName?.trim()) errors.familyName = 'Last name is required';
    }

    // The phone field only renders for admins, so only they can produce an invalid value.
    if (isAdminUser && userToEdit.type !== 'machine') {
      const { phoneNumber } = getPhoneNumberUpdate(userToEdit.phoneNumber, values.phoneNumber);
      if (typeof phoneNumber === 'string' && !PHONE_NUMBER_REGEX.test(phoneNumber)) {
        errors.phoneNumber = PHONE_NUMBER_ERROR;
      }
    }

    if (!values.permissions || values.permissions?.length < 1) {
      if (isAdminUser) {
        if (isOrgAccountingAdminUser) {
          errors.permissions = 'Please select at least one permission';
        }

        if (!isOrgAccountingAdminUser && !editUserHasOrgAccountingAdmin) {
          errors.permissions = 'Please select at least one permission';
        }
      }

      // since the orgAccountingAdmin can only add 1 permission, we need to check if the user has only that permission
      // before removing that permission from the user
      if (isOrgAccountingAdminUser) {
        if (userToEdit.permissions && userToEdit.permissions.length === 1 && editUserHasOrgAccountingAdmin) {
          errors.permissions = 'Please select at least one permission';
        }
      }
    }
    return errors;
  };

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    try {
      let permissionsValues: string[] = [];

      if (isAdminUser) {
        permissionsValues = Array.isArray(values.permissions)
          ? values.permissions.map((p: { value: string }) => p.value)
          : values.permissions?.value
            ? [values.permissions.value]
            : [];
      } else if (isOrgAccountingAdminUser && userToEdit.permissions) {
        // orgAccountingAdmin can only manage the orgAccountingAdmin permission; preserve all others
        const otherPerms = userToEdit.permissions
          .filter((permission: any) => permission.name !== UserPermissions.orgAccountingAdmin)
          .map((perm: any) => perm.name as string);

        const hasOrgAccountingAdmin = Array.isArray(values.permissions)
          ? values.permissions.some((p: { value: string }) => p.value === UserPermissions.orgAccountingAdmin)
          : values.permissions?.value === UserPermissions.orgAccountingAdmin;

        permissionsValues = [...otherPerms, ...(hasOrgAccountingAdmin ? [UserPermissions.orgAccountingAdmin] : [])];
      }

      const updatedUser = await editUser({
        organizationAlias: organizationAlias,
        userId: userToEdit.id,
        permissions: permissionsValues,
        ...(isAdminUser && userToEdit.type !== 'machine'
          ? {
              givenName: values.givenName.trim(),
              familyName: values.familyName.trim(),
              ...getPhoneNumberUpdate(userToEdit.phoneNumber, values.phoneNumber),
            }
          : isMissingName
            ? { givenName: values.givenName.trim(), familyName: values.familyName.trim() }
            : {}),
      }).unwrap();

      toast.success('User Saved');
      onSuccess(updatedUser);
    } catch (e: unknown) {
      toast.error(getUserFacingErrorMessage(e, 'Error editing user'));
    }
    setSubmitting(false);
  };
  const permissionOptions = useMemo(() => {
    const editedUserPermValues = new Set((userToEdit.permissions ?? []).map((p: any) => p.name as UserPermissions));
    return [
      { value: UserPermissions.reader, label: 'Read Only' },
      { value: UserPermissions.editor, label: 'Editor' },
      ...(showComfortPlanDealer ? [{ value: UserPermissions.comfortPlanDealer, label: 'Comfort Plan Dealer' }] : []),
      ...(internalUserIsEditingExternalUser &&
      !userPermissions.includes(UserPermissions.orgAccountingAdmin) &&
      !editedUserPermValues.has(UserPermissions.orgAccountingAdmin)
        ? [
            {
              value: UserPermissions.orgAccountingAdmin,
              label: 'Accounting Admin',
            },
          ]
        : []),
      ...[...new Set([...userPermissions, ...editedUserPermValues])]
        .filter(
          (perm) =>
            !BASE_PERMISSIONS.has(perm) &&
            !RESTRICTED_PERMISSIONS.has(perm) &&
            (internalUserIsEditingExternalUser ? EXTERNAL_ALLOWED_PERMISSIONS.has(perm) : true),
        )
        .map((perm) => ({
          value: perm,
          label: permissionMapper[perm] || perm,
          isDisabled:
            !userPermissions.includes(perm) &&
            internalUserIsEditingExternalUser &&
            perm !== UserPermissions.orgAccountingAdmin,
        })),
    ].toSorted((a, b) => a.label.localeCompare(b.label));
  }, [internalUserIsEditingExternalUser, userPermissions, showComfortPlanDealer, userToEdit.permissions]);
  const initialValues = useMemo(() => {
    return {
      email: userToEdit.email,
      machine: userToEdit.type === 'machine',
      givenName: userToEdit.givenName ?? '',
      familyName: userToEdit.familyName ?? '',
      phoneNumber: userToEdit.phoneNumber ?? '',
      permissions: permissionOptions?.filter((p) => userToEdit.permissions?.find((up: any) => up.name === p.value)),
    };
  }, [
    permissionOptions,
    userToEdit.email,
    userToEdit.familyName,
    userToEdit.givenName,
    userToEdit.permissions,
    userToEdit.phoneNumber,
    userToEdit.type,
  ]);

  return (
    <Formik
      initialValues={initialValues}
      validate={handleValidation}
      validateOnChange={false}
      onSubmit={handleSubmit}
      enableReinitialize={true}
    >
      {({ isSubmitting, setFieldValue }) => (
        <Form noValidate id="editUserForm">
          <Modal.Body background="secondary" overflow="auto" maxHeight="70vh">
            <Box childGap="lg">
              <Field
                type="email"
                label="Email Address"
                name="email"
                id="email"
                component={FormikTextInput}
                isRequired
                title="Emails cannot be edited after the user is created. You may remove this user and create a new one with the correct email."
                autoComplete="off"
                isDisabled
              />
              {canEditNames && (
                <Field
                  label="First Name"
                  name="givenName"
                  id="givenName"
                  component={FormikTextInput}
                  autoComplete="off"
                  isDisabled={isSubmitting}
                />
              )}
              {canEditNames && (
                <Field
                  label="Last Name"
                  name="familyName"
                  id="familyName"
                  component={FormikTextInput}
                  autoComplete="off"
                  isDisabled={isSubmitting}
                />
              )}
              {isAdminUser && (
                <Field
                  label="Phone Number"
                  name="phoneNumber"
                  id="phoneNumber"
                  component={FormikTextInput}
                  autoComplete="off"
                  isDisabled={isSubmitting}
                  helpText="Include area code (e.g. 555-555-1234 or +1-555-555-1234)"
                />
              )}
              <style>{`
                .permissions-disabled-value { background-color: var(--color-brand-grey-100) !important; }
                .permissions-disabled-label { color: var(--color-brand-grey-500) !important; }
                .permissions-disabled-remove { display: none !important; }
              `}</style>
              <Field
                label="Permissions"
                name="permissions"
                id="permissions"
                isMulti
                options={permissionOptions}
                component={FormikSelectInput}
                isDisabled={isSubmitting}
                isRequired
                menuPortalTarget={document.body}
                classNames={{
                  multiValue: ({ data }: any) => (data.isDisabled ? 'permissions-disabled-value' : ''),
                  multiValueLabel: ({ data }: any) => (data.isDisabled ? 'permissions-disabled-label' : ''),
                  multiValueRemove: ({ data }: any) => (data.isDisabled ? 'permissions-disabled-remove' : ''),
                }}
                helpText={
                  showComfortPlanDealer ? (
                    <Box fontSize="xs">
                      Note: Comfort Plan Dealer only affects the account creation flow. Users still need Editor or
                      other permissions to perform necessary operations.
                    </Box>
                  ) : undefined
                }
                onChange={(event: any) => {
                  const newValues: any[] = event.target.value ?? [];
                  const disabledSelected = permissionOptions.filter(
                    (opt: any) => opt.isDisabled && userToEdit.permissions?.some((p: any) => p.name === opt.value),
                  );
                  const merged = [...newValues];
                  for (const opt of disabledSelected) {
                    if (!merged.some((v) => v.value === opt.value)) {
                      merged.push(opt);
                    }
                  }
                  setFieldValue('permissions', merged);
                }}
              />
              <Field
                label="Machine to Machine"
                name="machine"
                id="machine"
                component={FormikToggle}
                title="The user type cannot be edited after the user is created. You may remove this user and create a new one with the correct type."
                isDisabled
              />
            </Box>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" tone="neutral" size="md" onClick={handleClose} isDisabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="md"
              type="submit"
              form="editUserForm"
              isLoading={isSubmitting}
              disabled={!allowsAction}
            >
              Save
            </Button>
          </Modal.Footer>
        </Form>
      )}
    </Formik>
  );
};

interface RemoveUserProps {
  handleClose: () => void;
  user?: Auth0PalmettoFinanceUser;
  organizationAlias: string;
}

const RemoveUser = ({ handleClose, organizationAlias, user = {} as Auth0PalmettoFinanceUser }: RemoveUserProps) => {
  const [removeUser] = useRemoveUserMutation();
  const allowsAction = useIsOrgReadOnly();

  const onClick = useCallback(async () => {
    try {
      await removeUser({ organizationAlias, userId: user.id }).unwrap();
      toast.success('User removed');
      handleClose();
    } catch (e: any) {
      toast.error(e?.data?.message || 'Error removing user');
    }
  }, [handleClose, organizationAlias, removeUser, user.id]);

  let name = user.nickname;
  if (user.givenName) {
    name = `${user.givenName} ${user.familyName}`;
  }
  return (
    <>
      <Modal.Body background="secondary" childGap="md">
        <Box>
          Are you sure you would like to remove {name} from {organizationAlias}? Doing so has the following impact
        </Box>
        <ul style={{ paddingLeft: '20px' }}>
          <li>
            They will no longer be able to access any accounts in {organizationAlias} or its child organizations.
          </li>
          <li>The user will not be deleted, and will still retain access to other palmetto systems.</li>
        </ul>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" tone="neutral" size="md" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          size="md"
          type="submit"
          form="addUserForm"
          variant="primary"
          tone="danger"
          onClick={onClick}
          disabled={!allowsAction}
        >
          Remove User
        </Button>
      </Modal.Footer>
    </>
  );
};

export const permissionMapper: { [key in UserPermissions]?: string } = {
  [UserPermissions.orgAccountingAdmin]: 'Accounting Admin',
  [UserPermissions.admin]: 'Admin',
  [UserPermissions.canApproveActivation]: 'Can Approve Activation',
  [UserPermissions.canApproveInstall]: 'Can Approve Install',
  [UserPermissions.canViewVendorInvoices]: 'Can View Vendor Invoices',
  [UserPermissions.contractAdmin]: 'Contract Admin',
  [UserPermissions.editor]: 'Editor',
  [UserPermissions.reader]: 'Reader',
  [UserPermissions.lightReachAccountingAdmin]: 'LightReach Accounting Admin',
  [UserPermissions.lightReachAccountingApprover]: 'LightReach Accounting Approver',
  [UserPermissions.lightReachAccountingSupport]: 'LightReach Accounting Support',
  [UserPermissions.lightReachCreditAdmin]: 'LightReach Credit Admin',
  [UserPermissions.lightReachCapitalMarketsAdmin]: 'LightReach Capital Markets Admin',
  [UserPermissions.lightReachOrgPaymentAdmin]: 'LightReach Org Payment Admin',
  [UserPermissions.lightReachOrgPaymentViewer]: 'LightReach Org Payment Viewer',
  [UserPermissions.lightReachPaymentAdmin]: 'LightReach Payment Admin',
  [UserPermissions.lightReachIncentiveAdmin]: 'LightReach Incentive Admin',
  [UserPermissions.lightReachPermissionsAdmin]: 'LightReach Permissions Admin',
  [UserPermissions.lightReachQCSettingsAdmin]: 'LightReach QC Settings Admin',
  [UserPermissions.lightReachQCTech]: 'LightReach QC Tech',
  [UserPermissions.lightReachRequirementAdmin]: 'LightReach Requirement Admin',
  [UserPermissions.lightReachSupport]: 'LightReach Support',
  [UserPermissions.lightReachTaskAdmin]: 'LightReach Task Admin',
  [UserPermissions.pricingAdmin]: 'Pricing Admin',
  [UserPermissions.reCheckAdmin]: 'Recheck Admin',
  [UserPermissions.packageAnalyzer]: 'Package Analyzer',
  [UserPermissions.comfortPlanDealer]: 'Comfort Plan Dealer',
  [UserPermissions.canImportAvlData]: 'Can Import AVL Data',
  [UserPermissions.lightReachAccountingReader]: 'LightReach Accounting Reader',
  [UserPermissions.lightReachAccountingSettings]: 'LightReach Accounting Settings',
  [UserPermissions.lightReachOrganizationSignatory]: 'LightReach Organization Signatory',
  [UserPermissions.measurableGenerationAdmin]: 'Measurable Generation Admin',
  [UserPermissions.lightReachSafeHarborEquipmentPricing]: 'LightReach Safe Harbor Equipment Pricing',
  [UserPermissions.billingBlockerAdmin]: 'Billing Blocker Admin',
  [UserPermissions.underwritingAdmin]: 'Underwriting Admin',
};

const OrganizationUsers = () => {
  const { alias } = useParams<{ alias: any }>();
  const { isPhone } = useBreakpoint();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParam = searchParams.get('search') || '';
  const page = searchParams.get('page') ? Number(searchParams.get('page')) : 1;
  const { isOpen: isAddOpen, handleClose: handleAddClose, handleToggle: handleAddToggle } = useOpenClose();
  const { isOpen: isRemoveOpen, handleClose: handleRemoveClose, handleToggle: handleRemoveToggle } = useOpenClose();
  const { isOpen: isModifyOpen, handleClose: handleModifyClose, handleToggle: handleModifyToggle } = useOpenClose();
  const [selectedUser, setSelectedUser] = useState<Auth0PalmettoFinanceUser | undefined>(undefined);
  const allowsAction = useIsOrgReadOnly();
  const { data: organizationData } = useGetOrganizationDetailsQuery({ alias });
  const organizationProgramTypes = organizationData?.programTypes || [];
  const location = useLocation();
  const usersRef = useRef<HTMLDivElement>(null);

  const {
    data: userData,
    error: searchError,
    refetch,
    isLoading,
    isFetching,
  } = useSearchUsersQuery({ orgAlias: alias, searchUsers: searchParam, page, limit: 10 }, { skip: !alias });

  const userColumns = [
    {
      heading: '',
      dataKey: 'picture',
      render: (cell: any) => {
        return <img src={cell} alt="user profile" style={{ maxHeight: '35px', maxWidth: '35px' }} />;
      },
      width: 83,
    },
    {
      heading: 'user',
      dataKey: 'userInfo',
      render: (_cell: any, row: any) => {
        return (
          <Box childGap="2xs">
            <Box fontWeight="medium">{row.name ?? row.nickname}</Box>
            <Box color="body-secondary">{row.email}</Box>
            {row.type === 'machine' && <Box color="body-secondary">Machine to Machine</Box>}
          </Box>
        );
      },
    },
    {
      heading: 'permissions',
      dataKey: 'permissions',
      render: (cell: any) => {
        return cell
          .map(
            (permission: { name: keyof UserPermissions }) =>
              permissionMapper[permission.name as keyof typeof UserPermissions] ?? permission.name,
          )
          .join(', ');
      },
    },
    {
      heading: 'email verified',
      dataKey: 'emailVerified',
      render: (cell: any) => (cell ? 'yes' : 'no'),
      width: 125,
    },
    {
      heading: 'last login (Local)',
      dataKey: 'lastLogin',
      render: (cell: any) => (cell ? <DateTime value={cell} /> : 'never'),
      width: 200,
    },
    {
      heading: '',
      render: (_cell: any, row: any) => {
        return (
          <Box gap="xs" direction="row" justifyContent="flex-end">
            <RequirePermissions
              permissions={[UserPermissions.admin, UserPermissions.orgAccountingAdmin]}
              checkAllPermissions={false}
            >
              <Button
                variant="secondary"
                tone="neutral"
                size="sm"
                isDisabled={usersLoading}
                onClick={() => {
                  // If the list is still refetching after a recent save, prefer the fresh API
                  // response (stored in selectedUser via onSuccess) over the stale table row.
                  const freshUser = isFetching && selectedUser?.id === row.id ? selectedUser : row;
                  setSelectedUser(freshUser);
                  handleModifyToggle();
                }}
              >
                Edit
              </Button>
            </RequirePermissions>
            <RequirePermissions permissions={[UserPermissions.admin]} checkAllPermissions={false}>
              <Button
                variant="secondary"
                tone="danger"
                size="sm"
                onClick={() => {
                  setSelectedUser(row);
                  handleRemoveToggle();
                }}
              >
                Remove
              </Button>
            </RequirePermissions>
          </Box>
        );
      },
    },
  ];

  const usersLoading = isLoading || isFetching;

  const onSearchChange = useMemo(
    () => (searchUsers: string) => {
      if (searchUsers && searchUsers.length >= 3) {
        searchParams.set('search', searchUsers);
      } else {
        searchParams.delete('search');
      }
      searchParams.delete('page');
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams],
  );

  const handlePageChange = (newPage: number) => {
    searchParams.set('page', newPage.toString());
    setSearchParams(searchParams);
  };

  const fetchUsers = useCallback(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    const { current } = usersRef;
    if (current !== null) {
      current.scrollIntoView({ behavior: 'instant', block: 'start' });
    }
  }, [location]);

  return (
    <>
      <Modal
        isOpen={isAddOpen}
        onDismiss={handleAddClose}
        maxWidth="4xl"
        overflow="auto"
        ariaLabelledBy="addOrgUserHeader"
      >
        <Modal.Header id="addOrgUserHeader" title="Add Organization User" onDismiss={handleAddClose} />
        <AddUser
          handleClose={() => {
            handleAddClose();
            fetchUsers();
          }}
          organizationAlias={alias || ''}
          organizationProgramTypes={organizationProgramTypes}
        />
      </Modal>

      <Modal
        isOpen={isRemoveOpen}
        onDismiss={handleRemoveClose}
        maxWidth="4xl"
        ariaLabelledBy="removeOrgUserHeader"
        fullScreenMobile
      >
        <Modal.Header
          id="removeOrgUserHeader"
          title={`Remove ${selectedUser?.name}?`}
          onDismiss={() => {
            handleRemoveClose();
            setSelectedUser(undefined);
          }}
        />
        <RemoveUser
          handleClose={() => {
            handleRemoveClose();
            setSelectedUser(undefined);
            fetchUsers();
          }}
          organizationAlias={alias || ''}
          user={selectedUser}
        />
      </Modal>

      <Modal
        isOpen={isModifyOpen}
        onDismiss={() => {
          handleModifyClose();
          setSelectedUser(undefined);
        }}
        maxWidth="4xl"
        ariaLabelledBy="modifyUserHeader"
        fullScreenMobile
      >
        <Box
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          padding="lg"
          borderColor="separator"
          borderWidth="0 0 xs 0"
          style={{ flexShrink: 0 }}
        >
          <Box
            as="h4"
            fontSize={{ base: 'md', tablet: 'lg' }}
            id="modifyUserHeader"
            style={{ minWidth: 0, wordBreak: 'break-word' }}
          >
            Edit {selectedUser?.name}
          </Box>
          <button
            aria-label="close"
            type="button"
            style={{
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              padding: 'var(--size-spacing-xs)',
              color: 'var(--color-text-body-primary)',
              lineHeight: 'var(--size-line-height-base)',
              borderRadius: 'var(--size-border-radius-sm)',
            }}
            onClick={() => {
              handleModifyClose();
              setSelectedUser(undefined);
            }}
          >
            <Icon name="remove-light" />
          </button>
        </Box>
        <EditUser
          handleClose={() => {
            handleModifyClose();
            setSelectedUser(undefined);
          }}
          onSuccess={(updatedUser) => {
            handleModifyClose();
            setSelectedUser(updatedUser);
            fetchUsers();
          }}
          organizationAlias={alias || ''}
          user={selectedUser}
          organizationProgramTypes={organizationProgramTypes}
        />
      </Modal>

      <Card ref={usersRef}>
        <Box
          gap="lg"
          padding="lg"
          alignItems="center"
          direction="row"
          justifyContent="space-between"
          style={{ display: 'flex' }}
        >
          <Box as="h3" fontWeight="medium" fontSize="md" width="5%">
            Users
          </Box>
          <Box style={{ marginRight: 'auto' }}>
            <Search
              setSearchText={(searchUsers: string) => onSearchChange(searchUsers)}
              searchText={searchParam}
              placeholder="Search by name or email"
              disabled={usersLoading}
              autoFocus
            />
          </Box>
          <RequirePermissions
            permissions={[UserPermissions.admin, UserPermissions.orgAccountingAdmin]}
            checkAllPermissions={false}
          >
            <Button
              onClick={handleAddToggle}
              variant="primary"
              tone="neutral"
              iconPrefix="add"
              aria-label="add user"
              size="sm"
              isLoading={usersLoading}
              disabled={!allowsAction}
            >
              {!isPhone && 'Add User'}
            </Button>
          </RequirePermissions>
        </Box>

        <PermissionsList />

        {!searchError && (
          <Box gap="md">
            <Table
              rowKey="id"
              columns={userColumns}
              rows={userData?.data || []}
              isLoading={usersLoading}
              isScrollable={{
                x: true,
                y: true,
              }}
            />
            {!usersLoading && searchError && (
              <Box gap="lg" padding="lg" alignItems="center">
                Error pulling users, please try again later or contact support
              </Box>
            )}
            {!usersLoading && (!userData?.data || userData?.data.length === 0) && (
              <Box gap="lg" padding="lg" alignItems="center">
                No users found
              </Box>
            )}
            <Box direction="row" childGap="xs" padding="0 lg lg lg">
              <Button
                isDisabled={!userData?.meta?.prevPage}
                onClick={() => handlePageChange(userData?.meta?.prevPage || 1)}
                variant="secondary"
                tone="neutral"
                iconPrefix="caret-left"
                size="sm"
              >
                Prev
              </Button>
              <Button
                isDisabled={
                  !userData?.meta?.nextPage ||
                  (userData?.data && userData?.meta.total !== undefined && userData?.meta.total < 10)
                }
                onClick={() => handlePageChange(userData?.meta?.nextPage || 1)}
                variant="secondary"
                tone="neutral"
                iconSuffix="caret-right"
                size="sm"
              >
                Next
              </Button>
            </Box>
          </Box>
        )}
      </Card>
    </>
  );
};

export default OrganizationUsers;
